import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Backdrop,
  Stack
} from "@mui/material";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon
} from "@mui/icons-material";
import Swal from "sweetalert2";
import io from "socket.io-client";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import CustomerAutocomplete from "./CustomerAutocomplete";
import FileAttachmentManager from "./FileAttachmentManager";
import MessageProgress from "./MessageProgress";
import { sendBulkWhatsAppMessage } from "../../api/whatsapp";

const MAX_PART_LENGTH = 4000;

function splitMessage(text) {
  if (!text || text.length <= MAX_PART_LENGTH) return [text || ''];
  const parts = [];
  let remaining = text;
  while (remaining.length > 0) {
    if (remaining.length <= MAX_PART_LENGTH) {
      parts.push(remaining);
      break;
    }
    let splitAt = remaining.lastIndexOf('\n', MAX_PART_LENGTH);
    if (splitAt <= 0) splitAt = remaining.lastIndexOf(' ', MAX_PART_LENGTH);
    if (splitAt <= 0) splitAt = MAX_PART_LENGTH;
    parts.push(remaining.substring(0, splitAt).trimEnd());
    remaining = remaining.substring(splitAt).trimStart();
  }
  return parts;
}

const WhatsAppBulkMessaging = () => {

  // State management
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, results: [] });
  const [showProgress, setShowProgress] = useState(false);

  // Dialog states
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);

  // Socket.IO setup for real-time progress
  const socketRef = useRef(null);

  useEffect(() => {
    // Determine Socket.IO URL based on environment
    const socketUrl =
      process.env.NODE_ENV === "production"
        ? undefined
        : process.env.REACT_APP_SOCKET_IO;

    // Initialize Socket.IO connection
    const socket = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
    });

    socketRef.current = socket;

    // Listen for real-time progress updates
    socket.on('whatsapp-bulk-progress', (progressData) => {
      console.log('📱 Progress update:', progressData);

      setSendProgress({
        sent: progressData.sent,
        total: progressData.total,
        successful: progressData.successful,
        failed: progressData.failed,
        results: progressData.results || []
      });

      // Show detailed status updates
      if (progressData.lastCustomer) {
        console.log(`📤 ${progressData.status}: ${progressData.lastCustomer}`);
      }

      // Handle completion event
      if (progressData.type === 'complete') {
        setIsSending(false);
        setShowProgress(false);

        // Show completion dialog based on results
        const { successful, failed, total } = progressData;

        if (successful === total) {
          // All jobs queued successfully
          Swal.fire({
            title: 'Queued!',
            text: `All ${successful} messages have been queued successfully. Check WhatsApp Queue for status updates.`,
            icon: 'success',
            timer: 5000,
            showConfirmButton: true,
            confirmButtonText: 'OK'
          });
        } else if (successful > 0 && failed > 0) {
          // Some queued, some failed
          Swal.fire({
            title: 'Partial Success',
            html: `
              <div style="text-align: left;">
                <p><strong>${successful} messages queued successfully</strong></p>
                <p><strong>${failed} messages failed to queue</strong></p>
                <p><small>Check WhatsApp Queue page for details.</small></p>
              </div>
            `,
            icon: 'warning',
            confirmButtonText: 'OK'
          });
        } else if (failed === total) {
          // All failed
          Swal.fire({
            title: 'Queue Failed',
            text: `All ${failed} messages failed to queue. Please check your connection and try again.`,
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }

        // Reset form on success
        if (successful > 0) {
          setMessage("");
          setSelectedCustomers([]);
          setAttachments([]);
        }
      }
    });

    socket.on('connect', () => {
      console.log('🔌 Socket.IO connected for WhatsApp bulk messaging');
    });

    socket.on('disconnect', () => {
      console.log('❌ Socket.IO disconnected from WhatsApp bulk messaging');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []);

  // Validation
  const isValidMessage = message.trim().length > 0;
  const hasRecipients = selectedCustomers.length > 0;
  const canSend = isValidMessage && hasRecipients && !isSending;

  // Character counter and message split preview
  const messageLength = message.length;
  const messageParts = splitMessage(message);
  const messagePartCount = messageParts.length;
  const isMultiPart = messagePartCount > 1;

  // Handle attachment addition
  const handleAttachmentAdd = (files) => {
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      type: file.type.startsWith('image/') ? 'image' : 'file',
      name: file.name,
      size: file.size,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  // Handle attachment removal
  const handleAttachmentRemove = (attachmentId) => {
    setAttachments(prev => {
      const attachment = prev.find(att => att.id === attachmentId);
      if (attachment && attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(att => att.id !== attachmentId);
    });
  };



  // Handle message sending
  const handleSendMessage = async () => {
    if (!canSend) return;

    try {
      // Show confirmation dialog
      const totalJobs = selectedCustomers.length * messagePartCount;
      const result = await Swal.fire({
        title: 'Send WhatsApp Messages',
        html: isMultiPart
          ? `<p>Send to <strong>${selectedCustomers.length}</strong> customer(s)?</p>
             <p>Message will be split into <strong>${messagePartCount} parts</strong> per recipient.</p>
             <p><small>Total: ${totalJobs} queue jobs will be created.</small></p>`
          : `<p>Send message to <strong>${selectedCustomers.length}</strong> customer(s)?</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, send it!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      setIsSending(true);
      setShowProgress(true);
      setSendProgress({ sent: 0, total: selectedCustomers.length, results: [] });

      // Show queuing started notification
      Swal.fire({
        title: 'Queuing Messages',
        html: `
          <div style="text-align: center;">
            <p>Adding ${selectedCustomers.length} message(s) to the queue...</p>
            <p><small>Messages will be sent automatically by the queue worker</small></p>
            <p><small>Track status in real-time on the WhatsApp Queue page</small></p>
          </div>
        `,
        icon: 'info',
        timer: 3000,
        showConfirmButton: false
      });

      // Prepare message data
      const messageData = {
        customers: selectedCustomers.map(c => ({
          _id: c._id,
          whatsapp: c.whatsapp,
          name: c.name,
          groupId: c.groupId
        })),
        message: message.trim(),
        attachments: attachments
      };

      // Send bulk message (progress will be tracked via Socket.IO)
      const response = await sendBulkWhatsAppMessage(messageData);

      // Basic response handling (detailed results will come via Socket.IO)
      if (!response.success) {
        throw new Error(response.message || 'Failed to send messages');
      }

      console.log('📤 Bulk queue jobs created successfully. Tracking progress via Socket.IO...');

    } catch (error) {
      console.error('Error sending bulk message:', error);

      // Reset loading state on error
      setIsSending(false);
      setShowProgress(false);

      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        Swal.fire({
          title: 'Request Timeout',
          html: `
            <div style="text-align: left;">
              <p>The request took longer than expected, but messages may still be sending in the background.</p>
              <p><strong>Please check:</strong></p>
              <ul style="text-align: left;">
                <li>WhatsApp Web connection status</li>
                <li>Backend logs for success confirmation</li>
                <li>Customer phones for received messages</li>
              </ul>
              <p><small>Consider reducing batch size or attachments for faster processing.</small></p>
            </div>
          `,
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to send messages. Please try again.',
          icon: 'error'
        });
      }
    }
  };

  // Preview message
  const handlePreviewMessage = () => {
    setOpenPreviewDialog(true);
  };

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      attachments.forEach(attachment => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });
    };
  }, [attachments]);

  return (
    <PageShell title="WhatsApp Bulk Messaging" subtitle="Send WhatsApp messages with attachments to multiple customers">
      <Grid container spacing={3}>
        {/* Left Column - Message Composition */}
        <Grid item xs={12} md={8}>
          <BentoCard bodySx={{ p: 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon color="success" />
                Compose Message
              </Typography>

              {/* Customer Selection */}
              <Box sx={{ mb: 3 }}>
                <CustomerAutocomplete
                  selectedCustomers={selectedCustomers}
                  setSelectedCustomers={setSelectedCustomers}
                />
              </Box>

              {/* Message Input */}
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                label="Message"
                placeholder="Type your WhatsApp message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                helperText={
                  isMultiPart
                    ? `${messageLength} characters — will be sent as ${messagePartCount} messages (≤${MAX_PART_LENGTH} chars each)`
                    : `${messageLength}/${MAX_PART_LENGTH} characters`
                }
                FormHelperTextProps={{
                  sx: { color: isMultiPart ? 'info.main' : 'text.secondary' }
                }}
                sx={{ mb: 2 }}
                disabled={isSending}
              />

              {/* Attachments Section */}
              <FileAttachmentManager
                attachments={attachments}
                onAttachmentAdd={handleAttachmentAdd}
                onAttachmentRemove={handleAttachmentRemove}
                disabled={isSending}
              />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={handleSendMessage}
                  disabled={!canSend}
                  fullWidth
                >
                  {isSending ? 'Queuing...' : 'Send Messages'}
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={handlePreviewMessage}
                  disabled={!isValidMessage || isSending}
                >
                  Preview
                </Button>
              </Box>
            </CardContent>
          </BentoCard>
        </Grid>

        {/* Right Column - Summary & Progress */}
        <Grid item xs={12} md={4}>
          {/* Message Summary */}
          <BentoCard sx={{ mb: 2 }} bodySx={{ p: 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Message Summary</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><PersonIcon /></ListItemIcon>
                  <ListItemText primary="Recipients" secondary={selectedCustomers.length} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><AttachFileIcon /></ListItemIcon>
                  <ListItemText primary="Attachments" secondary={attachments.length} />
                </ListItem>
                {isMultiPart && (
                  <ListItem>
                    <ListItemIcon><WhatsAppIcon color="info" /></ListItemIcon>
                    <ListItemText
                      primary="Messages per recipient"
                      secondary={`${messagePartCount} parts`}
                      secondaryTypographyProps={{ color: 'info.main' }}
                    />
                  </ListItem>
                )}
              </List>

              {selectedCustomers.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>Selected Customers:</Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {selectedCustomers.map(customer => (
                      <Chip
                        key={customer._id}
                        label={customer.name}
                        size="small"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </BentoCard>

          {/* Progress Section */}
          {showProgress && (
            <MessageProgress
              progress={sendProgress}
              customers={selectedCustomers}
            />
          )}
        </Grid>
      </Grid>



      {/* Message Preview Dialog */}
      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Message Preview
          {isMultiPart && (
            <Typography variant="caption" color="info.main" sx={{ ml: 1 }}>
              ({messagePartCount} parts)
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {messageParts.map((part, idx) => (
            <Box key={idx} sx={{ mb: isMultiPart ? 2 : 0 }}>
              {isMultiPart && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Part {idx + 1}/{messagePartCount} — {part.length} chars
                  {idx === 0 && attachments.length > 0 ? ' · Attachments included' : ''}
                </Typography>
              )}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e8f5e8' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {part}
                </Typography>
              </Paper>
            </Box>
          ))}
          {attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attachments {isMultiPart ? '(sent with Part 1)' : ''}:
              </Typography>
              {attachments.map(attachment => (
                <Chip
                  key={attachment.id}
                  label={attachment.name}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreviewDialog(false)} color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        open={isSending}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
      >
        <Stack spacing={3} alignItems="center">
          <CircularProgress
            color="inherit"
            size={60}
            variant={sendProgress.total > 0 ? "determinate" : "indeterminate"}
            value={sendProgress.total > 0 ? (sendProgress.sent / sendProgress.total) * 100 : 0}
          />
          <Typography variant="h6" color="inherit">
            Sending WhatsApp Messages...
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
            Please wait while we send your message{selectedCustomers.length > 1 ? 's' : ''} to {selectedCustomers.length} customer{selectedCustomers.length > 1 ? 's' : ''}
          </Typography>
          {sendProgress.total > 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" color="inherit" sx={{ fontWeight: 'bold' }}>
                Progress: {sendProgress.sent} / {sendProgress.total} sent
              </Typography>
              {sendProgress.successful > 0 && (
                <Typography variant="body2" color="inherit" sx={{ opacity: 0.9, color: '#4caf50' }}>
                  ✅ Successful: {sendProgress.successful}
                </Typography>
              )}
              {sendProgress.failed > 0 && (
                <Typography variant="body2" color="inherit" sx={{ opacity: 0.9, color: '#f44336' }}>
                  ❌ Failed: {sendProgress.failed}
                </Typography>
              )}
              <Typography variant="caption" color="inherit" sx={{ opacity: 0.7, mt: 1 }}>
                Real-time progress tracking
              </Typography>
            </Box>
          )}
        </Stack>
      </Backdrop>
    </PageShell>
  );
};

export default WhatsAppBulkMessaging;